/**
 * A channel's draft, as its owner (the merch CSR) reads it in the Draft step
 * (merch spec 12 §6; brief 01 §11): what Promo Office left to decide before
 * the draft is published.
 *
 * - `DraftChecksSummary`, at the head of the lines: where the draft came
 *   from, and what is still to check, or that nothing is.
 * - `DraftLineChecks`, under a line: the price list's other quantities (not
 *   quoted by the team), a possible alternative and the line it repeats, and
 *   a thin price (the channel's verdict on the team's price). A thin price is
 *   a warning only: the team's price stands and publishing is not stopped.
 *
 * The decisions themselves are the dialogs' (`DraftCheckDialogs`); the words
 * are shared-core's (`quote-draft-checks`).
 */
import type { ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import type { QuoteLineView, QuoteView } from "@shared-core/data/quote-types";
import {
  candidatesWords,
  describeDraftChecks,
  draftChecksOf,
  repeatedLineOf,
  thinPriceOf,
} from "@shared-core/domain/quote-draft-checks";
import { quoteLineNo } from "@shared-core/domain/quote-presentation";
import { cn } from "../../primitives/utils";

/** Where a channel's draft came from, and what is still to check before publishing. */
export function DraftChecksSummary({ quote }: { quote: QuoteView }) {
  const toCheck = describeDraftChecks(draftChecksOf(quote));
  return (
    <section
      aria-label="To check before publishing"
      className={cn(
        "rounded-md border px-3 py-2 text-sm",
        toCheck
          ? "border-warning/40 bg-warning/10"
          : "border-border bg-muted text-muted-foreground",
      )}
    >
      <p>
        Made from {quote.made_from_label}.{" "}
        {toCheck ? (
          <>
            <b className="font-semibold text-foreground">To check:</b> {toCheck}
            . Publishing drops any price-list quantity you have not chosen.
          </>
        ) : (
          "Everything Promo Office left open is decided. Publish when you're ready."
        )}
      </p>
    </section>
  );
}

function Note({
  tone,
  title,
  children,
}: {
  tone: "check" | "warning" | "danger";
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-2 text-xs">
      {tone !== "check" && (
        <TriangleAlert
          aria-hidden
          className={cn(
            "mt-0.5 size-3.5 shrink-0",
            tone === "danger"
              ? "text-red-700 dark:text-red-400"
              : "text-amber-700 dark:text-amber-400",
          )}
        />
      )}
      <p className="max-w-prose text-muted-foreground">
        <b
          className={cn(
            "font-semibold",
            tone === "danger"
              ? "text-red-700 dark:text-red-400"
              : tone === "warning"
                ? "text-amber-700 dark:text-amber-400"
                : "text-foreground",
          )}
        >
          {title}
        </b>{" "}
        {children}
      </p>
    </div>
  );
}

/** What is still to decide on one line of a channel's draft, and a thin price. Nothing when the line is clear. */
export function DraftLineChecks({
  quote,
  line,
}: {
  quote: QuoteView;
  line: QuoteLineView;
}) {
  const candidates = line.quantity_candidates ?? [];
  const repeated = line.is_possible_alternative
    ? repeatedLineOf(quote, line)
    : null;
  const thin = thinPriceOf(line);
  if (candidates.length === 0 && !line.is_possible_alternative && !thin)
    return null;
  return (
    <div className="space-y-1.5" data-section-id="quote.line_checks">
      {candidates.length > 0 && (
        <Note tone="check" title="Price-list quantities:">
          {candidatesWords(candidates)}. {candidates[0]!.source_label}. Choose
          which to offer.
        </Note>
      )}
      {line.is_possible_alternative && (
        <Note tone="check" title="Possible alternative:">
          {repeated
            ? `it repeats the article on ${quoteLineNo(quote, repeated)} ${repeated.name_full}.`
            : "it repeats an article on another line."}{" "}
          Make it a quantity option, keep it as an extra line, or remove it.
        </Note>
      )}
      {thin && (
        <Note tone={thin.tone} title={`${thin.headline}.`}>
          {thin.detail}
        </Note>
      )}
    </div>
  );
}
